import {inject, Injectable, NgZone} from '@angular/core';

export interface IScrollSpyMarker {
    id: string;
    element: HTMLElement;
}

export interface IScrollSpyHandle {
    disconnect(): void;
    navigateTo(id: string): void;
}

const SCROLL_SETTLE_FALLBACK_MS = 1000;

@Injectable({
    providedIn: 'root'
})
export class CategoryScrollSpyService {
    private ngZone = inject(NgZone);

    public observe(markers: IScrollSpyMarker[], onActiveIdChange: (id: string) => void): IScrollSpyHandle {
        if (!markers.length) {
            return {disconnect: () => undefined, navigateTo: () => undefined};
        }

        let observer: IntersectionObserver | undefined;
        let activeId: string | null = null;
        let suppressed = false;
        let resumeTimeoutId: ReturnType<typeof setTimeout> | undefined;

        const resume = () => {
            suppressed = false;
            window.removeEventListener('scrollend', resume);
            clearTimeout(resumeTimeoutId);
            recalculate();
        };

        const recalculate = () => {
            if (suppressed) {
                return;
            }

            const dominantId = this.getDominantMarkerId(markers);
            if (dominantId !== null && dominantId !== activeId) {
                activeId = dominantId;
                this.ngZone.run(() => onActiveIdChange(dominantId));
            }
        };

        this.ngZone.runOutsideAngular(() => {
            observer = new IntersectionObserver(recalculate, {threshold: [0, 0.25, 0.5, 0.75, 1]});
            markers.forEach((marker) => observer.observe(marker.element));
        });

        return {
            disconnect: () => {
                observer?.disconnect();
                window.removeEventListener('scrollend', resume);
                clearTimeout(resumeTimeoutId);
            },
            navigateTo: (id: string) => {
                activeId = id;
                suppressed = true;
                this.ngZone.run(() => onActiveIdChange(id));

                window.removeEventListener('scrollend', resume);
                clearTimeout(resumeTimeoutId);
                this.ngZone.runOutsideAngular(() => {
                    window.addEventListener('scrollend', resume, {once: true});
                    resumeTimeoutId = setTimeout(resume, SCROLL_SETTLE_FALLBACK_MS);
                });
            }
        };
    }

    private getDominantMarkerId(markers: IScrollSpyMarker[]): string | null {
        const viewportHeight = window.innerHeight;
        const sorted = [...markers].sort(
            (a, b) => a.element.getBoundingClientRect().top - b.element.getBoundingClientRect().top
        );

        let dominantId: string | null = null;
        let dominantCoverage = 0;

        sorted.forEach((marker, index) => {
            const top = marker.element.getBoundingClientRect().top;
            const nextTop = index < sorted.length - 1
                ? sorted[index + 1].element.getBoundingClientRect().top
                : Infinity;

            const visibleHeight = Math.min(nextTop, viewportHeight) - Math.max(top, 0);
            const coverage = Math.max(0, visibleHeight) / viewportHeight;

            if (coverage > dominantCoverage) {
                dominantCoverage = coverage;
                dominantId = marker.id;
            }
        });

        return dominantId;
    }
}
